import * as TagsInput from '@diceui/tags-input';
import { CircleX } from 'lucide-react';
import * as React from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  RecipientSuggestionList,
  useRecipientSuggestions,
} from './recipient-suggestions';
import type { RecipientSuggestion } from './recipient-suggestions';
import type { ComposerFormValues } from '../schema';
import { cn } from '~/lib/utils';

const emailSchema = z.email();

/** Nothing highlighted. Enter then adds what was typed, exactly as before — a
 *  suggestion is only ever chosen by walking into the list or clicking it. */
const NO_ACTIVE_SUGGESTION = -1;

type RecipientsFieldProps = {
  name: 'to' | 'cc';
  readOnly?: boolean;
  placeholder?: string;
};

export function RecipientsField({
  name,
  readOnly,
  placeholder,
}: RecipientsFieldProps) {
  const { field } = useController<ComposerFormValues, 'to' | 'cc'>({ name });

  return (
    <RecipientsBadgeInput
      recipients={field.value}
      onChange={(recipients) => field.onChange(normalizeRecipients(recipients))}
      inputRef={field.ref}
      onBlur={field.onBlur}
      readOnly={readOnly}
      placeholder={placeholder}
    />
  );
}

type RecipientsBadgeInputProps = {
  recipients: Array<string>;
  onChange: (recipients: Array<string>) => void;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  inputRef?: React.RefCallback<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
};

function RecipientsBadgeInput({
  recipients,
  onChange,
  readOnly,
  placeholder,
  className,
  inputRef,
  onBlur,
}: RecipientsBadgeInputProps) {
  const { t } = useTranslation('mailbox');
  const placeholderText = recipients.length > 0 || readOnly ? '' : placeholder;
  /**
   * What has been typed but not yet turned into a chip. The input stays
   * uncontrolled — tags-input clears it by assigning to the DOM node — so this
   * mirrors it rather than driving it, and picking a suggestion clears both.
   */
  const [query, setQuery] = React.useState('');
  const [isDismissed, setIsDismissed] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(NO_ACTIVE_SUGGESTION);
  const inputElement = React.useRef<HTMLInputElement | null>(null);
  const listId = React.useId();
  const optionId = (index: number) => `${listId}-option-${index}`;
  const suggestions = useRecipientSuggestions({
    query,
    recipients,
    enabled: !readOnly,
  });
  const isOpen = !readOnly && !isDismissed && suggestions.length > 0;
  const setInputRef = (node: HTMLInputElement | null) => {
    inputElement.current = node;

    inputRef?.(node);
  };

  function closeSuggestions() {
    setQuery('');
    setIsDismissed(false);
    setActiveIndex(NO_ACTIVE_SUGGESTION);
  }

  function selectSuggestion(suggestion: RecipientSuggestion) {
    if (inputElement.current) {
      inputElement.current.value = '';
      inputElement.current.focus();
    }

    closeSuggestions();
    onChange([...recipients, suggestion.email]);
  }

  /**
   * Runs before the tags-input handler, which skips its own work once the event
   * is defaulted — so preventing it is what keeps Enter from adding the partial
   * address instead of the highlighted suggestion.
   */
  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!isOpen) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const isDown = event.key === 'ArrowDown';

      event.preventDefault();
      /** Wraps within the list. Nothing-highlighted is the entry point, not a
       *  stop on the way round: leaving it needs one press either way. */
      setActiveIndex((index) =>
        isDown
          ? index >= suggestions.length - 1
            ? 0
            : index + 1
          : index <= 0
            ? suggestions.length - 1
            : index - 1,
      );

      return;
    }

    if (event.key === 'Escape') {
      /** The composer sits in a layer that closes on Escape, and dismissing a
       *  list should not also throw the draft away. */
      event.preventDefault();
      event.stopPropagation();
      setIsDismissed(true);
      setActiveIndex(NO_ACTIVE_SUGGESTION);

      return;
    }

    if (
      (event.key === 'Enter' || event.key === 'Tab') &&
      activeIndex !== NO_ACTIVE_SUGGESTION
    ) {
      const suggestion = suggestions[activeIndex];

      if (suggestion) {
        event.preventDefault();
        selectSuggestion(suggestion);
      }
    }
  }

  return (
    <div className="relative min-w-0 flex-1">
      <TagsInput.Root
        value={recipients}
        onValueChange={(next) => {
          closeSuggestions();
          onChange(next);
        }}
        onValidate={(recipient) =>
          emailSchema.safeParse(recipient.trim()).success
        }
        readOnly={readOnly}
        addOnPaste
        addOnTab
        blurBehavior="add"
        delimiter=","
        className={cn(
          'flex min-h-5 w-full flex-wrap items-center gap-2 bg-transparent',
          className,
        )}
      >
        {recipients.map((recipient) => (
          <TagsInput.Item
            key={recipient}
            value={recipient}
            className="inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium"
          >
            <TagsInput.ItemText className="max-w-44 truncate text-muted-foreground" />
            {!readOnly && (
              <TagsInput.ItemDelete
                className="rounded-full text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('Remove recipient')}
              >
                <CircleX className="size-2.5" strokeWidth={2.5} />
              </TagsInput.ItemDelete>
            )}
          </TagsInput.Item>
        ))}
        {!readOnly && (
          <TagsInput.Input
            ref={setInputRef}
            onBlur={(event) => {
              closeSuggestions();
              onBlur?.(event);
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              setIsDismissed(false);
              setActiveIndex(NO_ACTIVE_SUGGESTION);
            }}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listId : undefined}
            aria-activedescendant={
              isOpen && activeIndex !== NO_ACTIVE_SUGGESTION
                ? optionId(activeIndex)
                : undefined
            }
            placeholder={placeholderText}
            className="h-5 min-w-28 flex-1 border-0 bg-transparent p-0 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
        )}
      </TagsInput.Root>
      {isOpen && (
        <RecipientSuggestionList
          suggestions={suggestions}
          activeIndex={activeIndex}
          listId={listId}
          optionId={optionId}
          onActivate={setActiveIndex}
          onSelect={selectSuggestion}
        />
      )}
    </div>
  );
}

function normalizeRecipients(recipients: Array<string>) {
  return [...new Set(recipients.map((recipient) => recipient.trim()))].filter(
    Boolean,
  );
}
